import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, Lesson, LessonProgress } from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import {
  CheckCircle2,
  XCircle,
  ArrowLeft,
  ArrowRight,
  Lightbulb,
  BookOpen,
  Target,
  Clock,
  Trophy,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import MasterKaizenMessage from "../components/MasterKaizenMessage";
import { getSenseiMessage, getMessageType } from "../lib/senseiMessages";

export default function LessonPractice() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [progress, setProgress] = useState<LessonProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [startTime, setStartTime] = useState<number>(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const [nextLessonId, setNextLessonId] = useState<string | null>(null);
  const [autoAdvanceTimer, setAutoAdvanceTimer] =
    useState<NodeJS.Timeout | null>(null);
  const [showSenseiMessage, setShowSenseiMessage] = useState(false);
  const [senseiMessage, setSenseiMessage] = useState("");
  const [senseiMessageType, setSenseiMessageType] = useState<'encouragement' | 'wisdom' | 'celebration' | 'guidance'>('guidance');

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (lessonId) {
      loadLesson();
    }
  }, [lessonId, user]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
    };
  }, [autoAdvanceTimer]);

  const loadLesson = async () => {
    if (!lessonId) return;

    try {
      setLoading(true);
      const data = await api.startLesson(lessonId);
      setLesson(data.lesson);
      setProgress(data.progress);
      setStartTime(Date.now());

      // Handle lesson type routing
      const lessonType = data.lesson.lesson_type || "learn";

      // For forge lessons, always redirect to Practice Arena
      // The Practice Arena will handle XP warnings if already earned today
      if (lessonType === "forge" && data.lesson.technique_id) {
        const returnTo = searchParams.get("returnTo");
        const returnParam = returnTo
          ? `&returnTo=${encodeURIComponent(returnTo)}`
          : "";
        navigate(
          `/practice?mode=technique&techniqueId=${data.lesson.technique_id}&from=lesson&lessonId=${lessonId}${returnParam}`,
          { state: { lessonConfig: data.lesson.practice_config } },
        );
        return;
      }

      if (lessonType === "challenge" && data.lesson.technique_id) {
        const returnTo = searchParams.get("returnTo");
        const returnParam = returnTo
          ? `&returnTo=${encodeURIComponent(returnTo)}`
          : "";
        navigate(
          `/practice?mode=beat-the-clock&techniqueId=${data.lesson.technique_id}&from=lesson&lessonId=${lessonId}${returnParam}`,
          { state: { lessonConfig: data.lesson.practice_config } },
        );
        return;
      }

      // For Learn lessons, redirect to Technique Detail page with lesson tracking
      if (lessonType === "learn" && data.lesson.technique_id) {
        const returnTo = searchParams.get("returnTo");
        const returnParam = returnTo
          ? `&returnTo=${encodeURIComponent(returnTo)}`
          : "";
        navigate(
          `/technique/${data.lesson.technique_id}?lessonId=${lessonId}${returnParam}`,
        );
        return;
      }

      /* REMOVED: Old in-page Learn lesson logic
      if (lessonType === "learn" && data.lesson.technique_id) {
        try {
          const techniqueData = await api.getTechnique(
            data.lesson.technique_id,
          );
          setTechnique(techniqueData);
        } catch (err) {
          console.error("Failed to load technique:", err);
          // Continue without technique data - will just show lesson content
        }
      }
      */
    } catch (err) {
      console.error("Failed to load lesson:", err);
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to load lesson",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!lesson || !progress || !lessonId) return;

    const currentProblem = lesson.example_problems[currentProblemIndex];
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);

    // Check if answer is correct (normalize: remove spaces, lowercase)
    const normalizedAnswer = userAnswer
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");
    const normalizedCorrectAnswer = currentProblem.solution
      .toLowerCase()
      .replace(/\s+/g, "");
    const correct = normalizedAnswer === normalizedCorrectAnswer;

    setIsCorrect(correct);
    setShowResult(true);

    try {
      const result = await api.submitLessonAnswer(
        lessonId,
        correct ? 1 : 0, // 1 if correct, 0 if incorrect
        timeSpent,
        1, // 1 problem attempted
      );
      setProgress(result.progress);

      if (result.lesson_completed) {
        setLessonCompleted(true);
        setNextLessonId(result.next_lesson_id);
        setSenseiMessage(getSenseiMessage('lesson_complete'));
        setSenseiMessageType(getMessageType('lesson_complete'));
        setShowSenseiMessage(true);
        toast({
          title: "Ancient Form Mastered!",
          description: `You've achieved ${(Math.round(result.progress.current_accuracy * 10) / 10).toFixed(1)}% accuracy!`,
        });
      }

      // Auto-advance to next problem if answer is correct
      if (correct) {
        const timer = setTimeout(() => {
          handleNextProblem();
        }, 800); // 800ms delay - enough to see success, fast enough to keep flow
        setAutoAdvanceTimer(timer);
      }
    } catch (err) {
      console.error("Failed to submit answer:", err);
      toast({
        title: "Error",
        description: "Failed to submit answer. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleNextProblem = () => {
    if (!lesson) return;

    // Clear any pending auto-advance timer
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
      setAutoAdvanceTimer(null);
    }

    if (currentProblemIndex < lesson.example_problems.length - 1) {
      setCurrentProblemIndex(currentProblemIndex + 1);
    } else {
      // Restart from beginning for continued practice
      setCurrentProblemIndex(0);
    }

    setUserAnswer("");
    setShowResult(false);
    setIsCorrect(false);
    setShowExplanation(false);
    setStartTime(Date.now());
  };

  const handleGoToNextLesson = () => {
    if (nextLessonId) {
      navigate(`/lesson/${nextLessonId}`);
      // Reset state
      setLessonCompleted(false);
      setNextLessonId(null);
      setCurrentProblemIndex(0);
      setUserAnswer("");
      setShowResult(false);
      loadLesson();
    }
  };

  const getProgressPercentage = () => {
    if (!progress || !lesson) return 0;
    const criteriaProblems = lesson.completion_criteria.min_problems;
    return Math.min(
      (progress.problems_attempted / criteriaProblems) * 100,
      100,
    );
  };

  const meetsCompletionCriteria = () => {
    if (!progress || !lesson) return false;
    return (
      progress.problems_attempted >= lesson.completion_criteria.min_problems &&
      progress.current_accuracy >= lesson.completion_criteria.accuracy_threshold
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading lesson...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!lesson || !progress) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Lesson not found</p>
            <Button onClick={() => navigate("/arena")} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Arena
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Safety check for lessons without example problems
  if (!lesson.example_problems || lesson.example_problems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">
              This lesson has no practice problems. Please return to the
              curriculum.
            </p>
            <Button onClick={() => navigate("/arena")} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Arena
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentProblem = lesson.example_problems[currentProblemIndex];

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/arena")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Arena
        </Button>

        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">Lesson {lesson.lesson_number}</Badge>
              {progress.status === "completed" && (
                <Badge variant="default" className="bg-green-500">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Completed
                </Badge>
              )}
            </div>
            <h1 className="text-3xl font-bold">{lesson.title}</h1>
            <p className="text-muted-foreground mt-2">{lesson.description}</p>
          </div>
        </div>

        {/* Progress Stats */}
        <Card className="bg-accent/50">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Attempts</p>
                <p className="text-2xl font-bold">
                  {progress.problems_attempted}/
                  {lesson.completion_criteria.min_problems}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Accuracy</p>
                <p className="text-2xl font-bold">
                  {(Math.round(progress.current_accuracy * 10) / 10).toFixed(1)}
                  %
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Target</p>
                <p className="text-2xl font-bold">
                  {lesson.completion_criteria.accuracy_threshold}%
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-2xl font-bold">
                  {meetsCompletionCriteria() ? (
                    <span className="text-green-500">✓</span>
                  ) : (
                    <span className="text-yellow-500">●</span>
                  )}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Progress to completion
                </span>
                <span className="font-semibold">
                  {Math.round(getProgressPercentage())}%
                </span>
              </div>
              <Progress value={getProgressPercentage()} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lesson Completed Celebration */}
      {lessonCompleted && (
        <Card className="border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <CardContent className="pt-6">
            <div className="text-center relative">
              {/* Celebration Confetti Effect */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div
                  className="absolute top-0 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-ping"
                  style={{ animationDelay: "0s" }}
                ></div>
                <div
                  className="absolute top-0 left-3/4 w-2 h-2 bg-blue-400 rounded-full animate-ping"
                  style={{ animationDelay: "0.2s" }}
                ></div>
                <div
                  className="absolute top-0 left-1/2 w-2 h-2 bg-red-400 rounded-full animate-ping"
                  style={{ animationDelay: "0.4s" }}
                ></div>
                <div
                  className="absolute top-0 left-2/3 w-2 h-2 bg-purple-400 rounded-full animate-ping"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="absolute top-0 left-1/3 w-2 h-2 bg-green-400 rounded-full animate-ping"
                  style={{ animationDelay: "0.3s" }}
                ></div>
              </div>

              {/* Animated Trophy */}
              <div className="animate-in zoom-in-50 duration-700 delay-150">
                <Trophy className="w-16 h-16 text-green-500 mx-auto mb-4 animate-bounce" />
              </div>

              <h2 className="text-2xl font-bold mb-2 animate-in fade-in slide-in-from-bottom-3 duration-500 delay-300">
                Ancient Form Mastered!
              </h2>
              <p className="text-muted-foreground mb-4 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-500">
                You've achieved{" "}
                {(Math.round(progress.current_accuracy * 10) / 10).toFixed(1)}%
                accuracy with {progress.problems_attempted} problems!
              </p>
              <div className="flex gap-3 justify-center">
                {nextLessonId ? (
                  <Button onClick={handleGoToNextLesson} size="lg">
                    Next Lesson
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={() => navigate("/arena")} size="lg">
                    Back to Arena
                  </Button>
                )}
                <Button onClick={handleNextProblem} variant="outline" size="lg">
                  Continue Practicing
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Master's Guidance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{lesson.instructions}</p>
          {lesson.explanation && (
            <div className="mt-4 p-4 bg-accent/50 rounded-lg">
              <p className="font-semibold mb-2">Why this matters:</p>
              <p className="text-sm text-muted-foreground">
                {lesson.explanation}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Practice Problem */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Problem {currentProblemIndex + 1} of{" "}
            {lesson.example_problems.length}
          </CardTitle>
          <CardDescription>
            Solve the problem below and enter your answer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Problem */}
            <div className="p-6 bg-accent/30 rounded-lg text-center">
              <p className="text-3xl font-mono font-bold">
                {currentProblem.problem}
              </p>
            </div>

            {/* Answer Input */}
            {!showResult && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Your Answer:
                  </label>
                  <Input
                    type="text"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && userAnswer.trim()) {
                        handleSubmitAnswer();
                      }
                    }}
                    placeholder="Enter your answer"
                    className="text-2xl text-center font-mono h-16"
                    autoFocus
                  />
                </div>
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={!userAnswer.trim()}
                  className="w-full"
                  size="lg"
                >
                  Submit Answer
                </Button>
              </div>
            )}

            {/* Result */}
            {showResult && (
              <div className="space-y-4">
                <Card
                  className={
                    isCorrect
                      ? "border-green-500 bg-green-50"
                      : "border-red-500 bg-red-50"
                  }
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-4">
                      {isCorrect ? (
                        <>
                          <CheckCircle2 className="w-8 h-8 text-green-500" />
                          <div>
                            <h3 className="font-bold text-lg">Excellent, warrior! ⚔️</h3>
                            <p className="text-sm text-muted-foreground">
                              Well executed!
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-8 h-8 text-red-500" />
                          <div>
                            <h3 className="font-bold text-lg">
                              Not quite, young warrior
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              The correct answer is:{" "}
                              <span className="font-mono font-bold">
                                {currentProblem.solution}
                              </span>
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Show Steps */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowExplanation(!showExplanation)}
                      className="w-full"
                    >
                      <Lightbulb className="w-4 h-4 mr-2" />
                      {showExplanation ? "Hide" : "Reveal"} Technique Breakdown
                    </Button>

                    {showExplanation && (
                      <div className="mt-4 p-4 bg-white rounded-lg">
                        <p className="font-semibold mb-2">Technique Breakdown:</p>
                        <p className="text-sm whitespace-pre-wrap">
                          {currentProblem.steps}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Button
                  onClick={handleNextProblem}
                  className="w-full"
                  size="lg"
                >
                  {currentProblemIndex < lesson.example_problems.length - 1
                    ? "Next Problem"
                    : "Start Over"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Completion Criteria Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5" />
            Completion Criteria
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              {progress.problems_attempted >=
              lesson.completion_criteria.min_problems ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <Clock className="w-4 h-4 text-gray-400" />
              )}
              <span>
                Complete at least {lesson.completion_criteria.min_problems}{" "}
                problems ({progress.problems_attempted}/
                {lesson.completion_criteria.min_problems})
              </span>
            </div>
            <div className="flex items-center gap-2">
              {progress.current_accuracy >=
              lesson.completion_criteria.accuracy_threshold ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <Clock className="w-4 h-4 text-gray-400" />
              )}
              <span>
                Achieve {lesson.completion_criteria.accuracy_threshold}%
                accuracy (
                {(Math.round(progress.current_accuracy * 10) / 10).toFixed(1)}%)
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Master Kaizen Message Modal */}
      <MasterKaizenMessage
        show={showSenseiMessage}
        message={senseiMessage}
        messageType={senseiMessageType}
        onClose={() => setShowSenseiMessage(false)}
      />
    </div>
  );
}
